import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const imgStarCircle = '/icon/star-circle.svg'
const imgGridiconsQuote = '/icon/gridicons_quote.svg'
const imgArrowLeft02 = '/icon/arrow-left-02.svg'
const imgArrowRight02 = '/icon/arrow-right-02.svg'

const Reviews = () => {
  const reviews = useMemo(
    () => [
      {
        quote:
          'Notifyer transformed our customer communication. We went from manual WhatsApp messages to automated sequences that increased our response rate by 300%. The mobile apps are incredibly intuitive.',
        highlight: 'increased our response rate by 300%',
        name: 'Sarah Chen',
        title: 'Marketing Director',
        company: 'TechFlow Solutions',
        image: '/people/sarah-chen.png',
      },
      {
        quote:
          'The analytics dashboard gives us insights we never had before. We can see exactly when customers are most responsive and optimize our messaging accordingly.',
        highlight: 'insights we never had before',
        name: 'Emma Wilson',
        title: 'Sales Manager',
        company: 'GrowthCo',
        image: '/people/emma-wilson.png',
      },
      {
        quote:
          'Setting up was incredibly easy - took less than 10 minutes. The team management features help us coordinate customer support across multiple agents seamlessly.',
        highlight: 'took less than 10 minutes',
        name: 'David Kim',
        title: 'Operations Director',
        company: 'ServiceFirst',
        image: '/people/david-kim.png',
      },
      {
        quote:
          'The AI-assisted replies feature is a game-changer. It helps us respond faster while maintaining a personal touch. Our customer satisfaction scores have never been higher.',
        highlight: 'customer satisfaction scores have never been higher',
        name: 'Lisa Patel',
        title: 'Founder',
        company: 'StartupHub',
        image: '/people/lisa-patel.png',
      },
      {
        quote:
          'We tried other WhatsApp automation tools, but Notifyer stands out for its reliability and feature set. The bulk delivery optimization alone saved us hours every week.',
        highlight: 'saved us hours every week',
        name: 'James Thompson',
        title: 'Marketing Manager',
        company: 'RetailMax',
        image: '/people/james-thompson.png',
      },
    ],
    []
  )

  const extendedReviews = useMemo(() => {
    if (!reviews.length) return []
    const first = reviews[0]
    const last = reviews[reviews.length - 1]
    return [last, ...reviews, first]
  }, [reviews])

  const [activeIndex, setActiveIndex] = useState(1)
  const [isTransitioning, setIsTransitioning] = useState(true)
  const isSnappingRef = useRef(false)
  const intervalRef = useRef(null)
  const activeIndexRef = useRef(1)

  // Keep ref in sync with state
  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const goTo = useCallback(
    (nextIndex) => {
      if (isSnappingRef.current) return
      
      // Clamp the index to valid bounds
      const clampedIndex = Math.max(0, Math.min(nextIndex, extendedReviews.length - 1))
      
      setIsTransitioning(true)
      setActiveIndex(clampedIndex)
    },
    [extendedReviews.length]
  )

  const snapToIndex = useCallback((index) => {
    if (isSnappingRef.current) return
    
    isSnappingRef.current = true
    setIsTransitioning(false)
    
    // Use setTimeout for reliable timing - disable transition, update position, then re-enable
    setTimeout(() => {
      setActiveIndex(index)
      // Use requestAnimationFrame to ensure DOM update happens before re-enabling transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true)
          isSnappingRef.current = false
        })
      })
    }, 10)
  }, [])

  const handleTransitionEnd = useCallback(() => {
    if (extendedReviews.length === 0 || isSnappingRef.current) return
    
    if (activeIndex === extendedReviews.length - 1) {
      snapToIndex(1)
    } else if (activeIndex === 0) {
      snapToIndex(extendedReviews.length - 2)
    }
  }, [activeIndex, extendedReviews.length, snapToIndex])

  // Auto-advance interval - set up once, use refs for current values
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    
    // Set up the interval - it will use refs to get current values
    intervalRef.current = setInterval(() => {
      if (!isSnappingRef.current && extendedReviews.length > 0) {
        const currentIndex = activeIndexRef.current
        const nextIndex = currentIndex + 1
        // Only advance if we're not at the duplicate end
        if (nextIndex < extendedReviews.length) {
          goTo(nextIndex)
        }
      }
    }, 3000)
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [goTo, extendedReviews.length])

  useEffect(() => {
    setActiveIndex(1)
    isSnappingRef.current = false
  }, [reviews.length])

  const normalizedIndex = reviews.length ? (activeIndex - 1 + reviews.length) % reviews.length : 0

  return (
    <section className="bg-white px-4 py-10 sm:px-8 sm:py-12 lg:px-[100px] lg:py-16">
      <div className="mx-auto flex max-w-[1240px] flex-col items-center gap-10 sm:gap-12 text-center">
        {/* Pill */}
        <div className="flex items-center gap-2 rounded-full bg-[#fafafa] px-4 py-2 text-xs font-medium uppercase tracking-[0.28px] text-[#1c2329] shadow-[inset_0px_1px_1px_rgba(26,32,50,0.1),inset_0px_1px_2px_rgba(26,32,50,0.05)]">
          <img src={imgStarCircle} alt="" className="h-6 w-6" />
          <span>Testimonials</span>
        </div>

        {/* Heading */}
        <div className="space-y-2 px-1 sm:px-0">
          <h2 className="text-[28px] font-semibold leading-tight tracking-[-1px] text-[#1c2329] sm:text-[38px] md:text-[48px] lg:text-[56px]">
            Proven results, real experiences
          </h2>
          <p className="text-[15px] leading-[1.7] tracking-[-0.5px] text-[#656f68] sm:text-[17px]">
            Join thousands of businesses that trust Notifyer for their WhatsApp automation needs.
          </p>
        </div>

        {/* Testimonial slider */}
        <div className="relative w-full overflow-hidden rounded-[14px]">
          <div
            className={`flex w-full ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedReviews.map((review, idx) => (
              <div
                key={review.name}
                className="flex w-full shrink-0 flex-col items-center gap-6 sm:gap-7 lg:flex-row lg:items-start lg:gap-8"
                aria-hidden={activeIndex !== idx}
              >
                <div className="h-[240px] w-[220px] overflow-hidden rounded-[16px] shadow-[0px_6px_14px_rgba(0,0,0,0.08)] sm:h-[280px] sm:w-[240px] lg:h-[320px] lg:w-[260px]">
                  <img
                    src={review.image}
                    alt={`${review.name} portrait`}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="flex-1 rounded-[12px] border border-[#f2f3f3] bg-white p-2 shadow-[0px_8px_30px_rgba(0,0,0,0.02)]">
                  <div className="h-full rounded-[10px] border border-[#f3f3f2] bg-white px-5 py-6 text-left sm:px-7 sm:py-8">
                    <img src={imgGridiconsQuote} alt="" className="h-9 w-9 sm:h-10 sm:w-10" />
                    <p className="mt-5 text-[17px] leading-[1.7] tracking-[-0.4px] text-[#1c2329b3] sm:mt-6 sm:text-[20px]">
                      {review.quote.includes(review.highlight)
                        ? review.quote.split(review.highlight)[0]
                        : review.quote}
                      {review.quote.includes(review.highlight) && (
                        <>
                          <span className="font-semibold text-[#1c2329]">{review.highlight}</span>
                          {review.quote.split(review.highlight)[1]}
                        </>
                      )}
                    </p>
                    <div className="mt-5 sm:mt-6">
                      <p className="text-[17px] font-semibold tracking-[-0.5px] text-[#1c2329] sm:text-[18px]">
                        {review.name}
                      </p>
                      <p className="text-[14px] leading-[1.6] text-[#707070] sm:text-[15px]">
                        {review.title} • <span className="font-semibold text-[#01cd74]">{review.company}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={() => goTo(activeIndex - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(28,35,41,0.15)] bg-white transition hover:-translate-x-0.5 hover:shadow-md"
              aria-label="Previous testimonial"
            >
              <img src={imgArrowLeft02} alt="" className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <div className="flex items-center gap-3">
              {reviews.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => goTo(idx + 1)}
                  className={`h-2 rounded-full transition-all ${
                    normalizedIndex === idx ? 'w-8 bg-[#1c2329]' : 'w-5 bg-[#e4e4e3]'
                  }`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1c2329] bg-white transition hover:translate-x-0.5 hover:shadow-md"
              aria-label="Next testimonial"
            >
              <img src={imgArrowRight02} alt="" className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Reviews

